import * as _ from "lodash";
import * as hash from 'object-hash';
import { useEffect, useState } from "react"
import { Dispatch, connect } from "react-redux";
import { IDonorProfileAddress, IDonorProfileAddressLookupData, IDonorProfileAddressPost, IDonorProfileHeaderInfo, IUsPsAddressResponse } from '@nkr/models/index';
import {
    IApplicationState, AppActionCreators, IAppState,
    DonorActionCreators, IDonorProfileDonorAddressResponse, IDonorProfileDonorAddressLookupDataResponse, IDonorProfileDonorAddressPostResponse, IAddressValidationResponse
} from '@nkr/store/index';
import { toast } from "react-toastify";
import { IBaseDispatchProps, ToggleType } from '../models';
import { IValidation } from './validation';
import PersonalInformation from './personal-information';
import usePrevious from '@nkr/services/use-previous';
import AddressValidation, { IAddress } from '@nkr/components/address-validation';
import DuplicateRecordModal from '@nkr/components/duplicate-record-modal';

interface IOwnProps {
    centerId: number,
    activeKey: string,
    isAdmin: boolean,
    donorId: number,
    headerInfo: IDonorProfileHeaderInfo,
    hasDataChanged: boolean,
    saveRequested: boolean,
    refreshRequested: boolean,
    onDataChanged: (hasDataChanged: boolean) => void,
    onSaveFinished: (refreshHeader: boolean) => void,
    onRefreshFinished: () => void,
    onResetDataChangedWarning: () => void,
    onTabClick: (href: string) => void,
    onUpdateForErrors: (toggleType: ToggleType, messages: string[]) => void
}

interface IStateProps {
    app: IAppState,
    groupIsSuper: boolean
}

interface IDispatchProps extends IBaseDispatchProps {
    getProfileDonorAddress: (id: number, centerId: number) => Promise<IDonorProfileDonorAddressResponse>,
    getProfileDonorAddressLookupData: (centerId: number) => Promise<IDonorProfileDonorAddressLookupDataResponse>,
    postProfileDonorAddress: (id: number, centerId: number, addressData: IDonorProfileAddressPost) => Promise<IDonorProfileDonorAddressPostResponse>,
    validateAddress: (donorId: number, centerId: number, address: string, address2: string, city: string, state: string, postalCode: string) => Promise<IAddressValidationResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

const initialValidation: IValidation = {
    fullName: true,
    addressLine1: true,
    city: true,
    state: true,
    postalCode: true,
    email: true,
    phone: true,
    phone2: true
};

interface IAddressValidation {
    show: boolean,
    response: IUsPsAddressResponse
    submittedAddress: IAddress
}

const DonorAddress = (props: TProps) => {
    const [data, setData] = useState<IDonorProfileAddress>(null);
    const [lookupData, setLookupData] = useState<IDonorProfileAddressLookupData>({ stateList: [], countryList: [] });
    const [validation, setValidation] = useState<IValidation>(initialValidation);
    const [addressValidation, setAddressValidation] = useState<IAddressValidation>({ show: false, response: null, submittedAddress: null });
    const [dirty, setDirty] = useState(false);
    const [donorDataHash, setDonorDataHash] = useState('');
    const [showDuplicateRecord, setShowDuplicateRecord] = useState(false);
    const [duplicateMessage, setDuplicateMessage] = useState<string>('');

    const previousSaveRequested = usePrevious(props.saveRequested);
    const previousRefreshRequested = usePrevious(props.refreshRequested);

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        if (previousSaveRequested == false && props.saveRequested == true) {
            handleValidate();
        }

        if (previousRefreshRequested == false && props.refreshRequested == true) {
            getData();
        }
    }, [props.saveRequested, props.refreshRequested]);

    const getData = () => {
        let { centerId, donorId } = props;

        props.showLoading('Getting address data, one moment please...').then(() => {
            Promise.all([
                props.getProfileDonorAddress(donorId, centerId),
                props.getProfileDonorAddressLookupData(centerId)
            ])
                .then(([dataResponse, lookupDataResponse]) => {
                    if (dataResponse.data.success && lookupDataResponse.data.success) {
                        let data = dataResponse.data.results;
                        let newDonorDataHash = hash(data);

                        setData(data);
                        setLookupData(lookupDataResponse.data.results);
                        setDonorDataHash(newDonorDataHash);
                    }
                })
                .catch(() => {
                })
                .finally(() => {
                    props.hideLoading();

                    if (props.refreshRequested) {
                        props.onRefreshFinished();
                    }
                });
        });
    }

    const save = async (addressLine1: string, addressLine2: string, city: string, state: string, postalCode: string) => {
        let { centerId, donorId } = props;

        let addressData: IDonorProfileAddressPost = {
            fullName: data.fullName,
            country: data.country,
            addressLine1,
            addressLine2,
            city,
            state,
            postalCode,
            phone: data.phone,
            phone2: data.phone2,
            email: data.email,
            source: data.source
        };

        props.showLoading('Saving address data, one moment please...').then(() => {
            props.postProfileDonorAddress(donorId, centerId, addressData)
                .then((response) => {
                    if (response.data.success && response.data.results) {
                        let newData = { ...data, addressLine1, city, state, postalCode };

                        setData(newData);

                        let newDonorDataHash = hash(newData);

                        setDonorDataHash(newDonorDataHash);

                        props.onDataChanged(false);
                        props.onResetDataChangedWarning();
                        props.onUpdateForErrors(ToggleType.Success, []);
                        props.onSaveFinished(false);
                    }
                })
                .catch((error) => {
                    if (error.response.status == 409) {
                        setDuplicateMessage(error.response.data);
                        setShowDuplicateRecord(true);
                        props.onSaveFinished(false);

                        return;
                    }

                    toast.error(error.response.data);
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleSubmitWithAddress = (recommended: boolean) => {
        let { addressLine1, addressLine2, city, state, postalCode } = data

        if (recommended) {
            addressLine1 = addressValidation.response.address1;
            addressLine2 = addressValidation.response.address2;
            city = addressValidation.response.city;
            state = addressValidation.response.state;
            postalCode = addressValidation.response.zip5;
        }

        setAddressValidation((prev) => ({
            ...prev,
            show: false
        }));

        save(addressLine1, addressLine2, city, state, postalCode);
    };

    const handleCancelAddressSelection = () => {
        setAddressValidation((prev) => ({
            ...prev,
            show: false
        }));

        props.onSaveFinished(false);
    }

    const handleDataChange = (data: IDonorProfileAddress) => {
        let newData = _.cloneDeep(data);
        let newHash = hash(newData);

        let hasDataChanged = newHash != donorDataHash;

        setData(newData);

        props.onDataChanged(hasDataChanged);
    }

    const handleValidate = async () => {
        let { centerId, donorId } = props;

        let newData = _.cloneDeep(data);

        let newValidation: IValidation = {
            fullName: true,
            addressLine1: true,
            city: true,
            state: true,
            postalCode: true,
            email: true,
            phone: true,
            phone2: true
        };

        let messages: string[] = []

        let { phone, phone2, fullName, addressLine1, addressLine2, city, state, email, country, postalCode } = newData

        if (phone) {
            let phoneFormatted = phone.replace(/[-]/g, '')

            if (phoneFormatted != '' && phoneFormatted.length != 10) {
                messages.push('Phone # is not in the proper format. It must be 10 numbers without any formatting. ex: 1235551212 or 123-555-1212');
                newValidation.phone = false;
            }
        }

        if (phone2) {
            let phone2Formatted = phone2.replace(/[-]/g, '')

            if (phone2Formatted != '' && phone2Formatted.length != 10) {
                messages.push('Secondary Phone # is not in the proper format. It must be 10 numbers without any formatting. ex: 1235551212 or 123-555-1212');
                newValidation.phone2 = false;
            }
        }

        if (!fullName) {
            messages.push('Full Name is required.')
            newValidation.fullName = false;
        }

        if (addressLine1) {
            let isNumeric = /^[0-9]+$/.test(addressLine1);

            if (isNumeric) {
                messages.push('Address is not in the proper format.')
                newValidation.addressLine1 = false;
            }
        }

        if (email) {
            let emailFormat = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email);

            if (!emailFormat) {
                messages.push('Email is not in the proper format.')
                newValidation.email = false;
            }
        } else {
            messages.push('Email is required.')
            newValidation.email = false;
        }

        if (country == 1) {
            let regexp = /^[0-9]{5}$/;

            if (!regexp.test(postalCode)) {
                messages.push('Postal code is not in the correct format. Please make sure it is a 5 digit number. ')
                newValidation.postalCode = false;
            }
        } else {
            if (!postalCode) {
                messages.push('Postal Code is missing')
                newValidation.postalCode = false;
            }
        }

        let addressValid = true;

        if (messages.length == 0) {
            let addressValidationResponse = await props.validateAddress(
                donorId,
                centerId,
                addressLine1,
                addressLine2,
                city,
                state,
                postalCode
            );

            if (addressValidationResponse.data.success == false) {
                addressValid = false;

                setAddressValidation({
                    show: true,
                    response: addressValidationResponse.data.results,
                    submittedAddress: {
                        street: addressLine1,
                        street2: addressLine2,
                        city,
                        state,
                        postalCode
                    }
                });
            }
        }

        setValidation(newValidation);

        if (messages.length == 0 && addressValid) {
            setDirty(false);
            save(addressLine1, addressLine2, city, state, postalCode);
        } else {
            setData(newData);
            setDirty(true);

            props.onUpdateForErrors(ToggleType.Errors, messages);
            props.onSaveFinished(false);
        }
    }

    const handleCloseDuplicateRecord = () => {
        setShowDuplicateRecord(false);
    };

    if (props.headerInfo == null || data == null)
        return null;

    return (
        <>
            <PersonalInformation
                data={data}
                lookupData={lookupData}
                validation={validation}
                dirty={dirty}
                onChange={handleDataChange}
            />

            <AddressValidation
                show={addressValidation.show}
                response={addressValidation.response}
                submittedAddresss={addressValidation.submittedAddress}
                onUseAddress={handleSubmitWithAddress}
                onCancel={handleCancelAddressSelection}
            />

            <DuplicateRecordModal
                show={showDuplicateRecord}
                message={duplicateMessage}
                onClose={handleCloseDuplicateRecord}
            />
        </>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app,
        groupIsSuper: appState.app.user.group.isSuper
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getProfileDonorAddress: (id, centerId) => {
            return dispatch(DonorActionCreators.getProfileDonorAddress(id, centerId));
        },
        getProfileDonorAddressLookupData: (centerId) => {
            return dispatch(DonorActionCreators.getProfileDonorAddressLookupData(centerId));
        },
        postProfileDonorAddress: (id, centerId, data) => {
            return dispatch(DonorActionCreators.postProfileDonorAddress(id, centerId, data));
        },
        validateAddress: (donorId, centerId, address, address2, city, state, postalCode) => {
            return dispatch(DonorActionCreators.validateAddress(donorId, centerId, address, address2, city, state, postalCode));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(DonorAddress);